/** SUNTRONIX ONE — Product Catalogue Phase 2: Collections + Catalogue Builder.
 *  Internal Sales / Technology tool. Build curated product sets and push
 *  them straight into the CRM quotation composer via "Add to Quote". */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Layers, Plus, Search, Trash2, X, ChevronLeft, FileText, Package,
  Tag, ShoppingCart, PencilLine, Boxes, Share2, Copy, Globe,
} from "lucide-react";

const fmtINR = (n) =>
  n ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n)
    : "Price on request";

const STATUS_TONE = {
  DRAFT: "bg-amber-50 text-amber-800 border-amber-200",
  PUBLISHED: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function useEscape(onClose) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
}


export default function CatalogueCollections() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/catalogue-collections",
        { params: q.trim() ? { q: q.trim() } : {} });
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  if (openId) {
    return <CollectionBuilder cid={openId} onBack={() => { setOpenId(null); load(); }} />;
  }

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="collections-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Sales Desk · Product Catalogue
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <Layers className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
            Collections
          </h1>
          <p className="text-[12px] text-slate-500 mt-2 max-w-2xl">
            Curated, reusable product sets. Build a collection, then add it straight to a quotation.
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="h-9 bg-slate-900 text-white hover:bg-slate-800"
          data-testid="new-collection-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New collection
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search collections by name, tag…" className="h-9 pl-8"
            data-testid="collections-search" />
        </div>
        <div className="text-[12px] text-slate-500">{rows.length} collection{rows.length === 1 ? "" : "s"}</div>
      </div>

      {busy && rows.length === 0 ? (
        <div className="text-center py-16 text-slate-500">Loading collections…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 border border-dashed rounded-md" data-testid="collections-empty">
          <Boxes className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="text-[14px] text-slate-600">No collections yet.</div>
          <Button onClick={() => setNewOpen(true)} variant="outline" className="mt-4 h-9"
            data-testid="collections-empty-new">
            <Plus className="h-4 w-4 mr-1.5" /> Create your first collection
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((c) => (
            <button key={c.id} onClick={() => setOpenId(c.id)}
              data-testid={`collection-card-${c.id}`}
              className="text-left bg-white border border-slate-200 rounded-md overflow-hidden
                hover:border-slate-900 hover:shadow-md transition-all group">
              <div className="h-28 bg-slate-100 grid place-items-center overflow-hidden">
                {c.cover_image_url
                  ? <img src={c.cover_image_url} alt={c.name} className="h-full w-full object-cover" />
                  : <Layers className="h-9 w-9 text-slate-300 group-hover:text-indigo-400 transition-colors" />}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[15px] font-semibold text-slate-900 line-clamp-1">{c.name}</div>
                  <span className={`shrink-0 text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border ${STATUS_TONE[c.status] || STATUS_TONE.DRAFT}`}>
                    {c.status}
                  </span>
                </div>
                {c.description && <div className="text-[12px] text-slate-500 mt-1 line-clamp-2">{c.description}</div>}
                <div className="text-[11px] text-slate-400 mt-3 flex gap-3">
                  <span>{c.line_count} product{c.line_count === 1 ? "" : "s"}</span>
                  <span>·</span>
                  <span>{c.unit_count} unit{c.unit_count === 1 ? "" : "s"}</span>
                </div>
                {(c.tags || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.tags.slice(0, 4).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 text-[10px] text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">
                        <Tag className="h-2.5 w-2.5" />{t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {newOpen && (
        <NewCollectionDialog onClose={() => setNewOpen(false)}
          onCreated={(id) => { setNewOpen(false); load(); setOpenId(id); }} />
      )}
    </div>
  );
}


function NewCollectionDialog({ onClose, onCreated }) {
  useEscape(onClose);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/catalogue-collections", {
        name: name.trim(), description: description.trim() || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Collection created");
      onCreated(data.collection.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="new-collection-dialog">
      <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-slate-900">New collection</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Name *</div>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Enterprise Laptop Refresh 2026" data-testid="new-collection-name" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Description</div>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What this collection is for…" data-testid="new-collection-desc" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Tags (comma separated)</div>
            <Input value={tags} onChange={(e) => setTags(e.target.value)}
              placeholder="laptops, apple, executive" data-testid="new-collection-tags" />
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" className="flex-1 h-10" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 h-10 bg-slate-900 text-white hover:bg-slate-800"
            onClick={submit} disabled={busy} data-testid="new-collection-save">
            {busy ? "Creating…" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function CollectionBuilder({ cid, onBack }) {
  const navigate = useNavigate();
  const [col, setCol] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editMeta, setEditMeta] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/catalogue-collections/${cid}`);
      setCol(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const setQty = async (code, qty) => {
    if (qty < 1) return removeItem(code);
    try {
      const { data } = await api.patch(`/catalogue-collections/${cid}/items/${code}`, { quantity: qty });
      setCol(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const removeItem = async (code) => {
    try {
      const { data } = await api.delete(`/catalogue-collections/${cid}/items/${code}`);
      setCol(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };
  const addProduct = async (product, qty = 1) => {
    try {
      const { data } = await api.post(`/catalogue-collections/${cid}/items`,
        { product_code: product.code, quantity: qty });
      setCol(data);
      toast.success(`Added ${product.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };

  const addToQuote = async () => {
    if (!col?.items?.length) { toast.error("Add products first"); return; }
    try {
      const { data } = await api.get(`/catalogue-collections/${cid}/quote-lines`);
      if (!data.items?.length) { toast.error("No priced products to quote"); return; }
      sessionStorage.setItem("st:quote-prefill",
        JSON.stringify({ items: data.items, subject: data.subject_hint || "" }));
      navigate("/commercial/crm/quotations?new=1&prefill=1");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build quote"); }
  };

  const del = async () => {
    if (!window.confirm("Delete this collection? This cannot be undone.")) return;
    try { await api.delete(`/catalogue-collections/${cid}`); toast.success("Deleted"); onBack(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggleStatus = async () => {
    const next = col.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";
    try {
      const { data } = await api.patch(`/catalogue-collections/${cid}`, { status: next });
      setCol(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  if (!col) {
    return <div className="text-center py-16 text-slate-500">{busy ? "Loading…" : "Not found"}</div>;
  }
  const subtotal = (col.items || []).reduce(
    (s, it) => s + (it.list_price_inr || 0) * (it.quantity || 0), 0);

  return (
    <div className="space-y-5 max-w-[1400px]" data-testid="collection-builder">
      <button onClick={onBack} className="text-[12px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
        data-testid="collection-back">
        <ChevronLeft className="h-4 w-4" /> All collections
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-heading font-semibold text-slate-900">{col.name}</h1>
            <span className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border ${STATUS_TONE[col.status] || STATUS_TONE.DRAFT}`}>
              {col.status}
            </span>
            <button onClick={() => setEditMeta(true)} className="text-slate-400 hover:text-slate-900"
              data-testid="collection-edit-meta"><PencilLine className="h-4 w-4" /></button>
          </div>
          {col.description && <p className="text-[13px] text-slate-500 mt-1 max-w-2xl">{col.description}</p>}
          <div className="text-[11px] text-slate-400 mt-2 flex gap-3" data-testid="collection-totals">
            <span>{col.line_count} product{col.line_count === 1 ? "" : "s"}</span>
            <span>·</span><span>{col.unit_count} unit{col.unit_count === 1 ? "" : "s"}</span>
            <span>·</span><span>Indicative {fmtINR(subtotal)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline"
            className={`h-9 ${col.status === "PUBLISHED" ? "" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
            onClick={toggleStatus} data-testid="collection-toggle-status">
            {col.status === "PUBLISHED" ? "Unpublish" : "Publish"}
          </Button>
          <Button variant="outline" className="h-9" onClick={() => setAddOpen(true)} data-testid="collection-add-products">
            <Plus className="h-4 w-4 mr-1.5" /> Add products
          </Button>
          <Button variant="outline" className="h-9" onClick={() => setShareOpen(true)} data-testid="collection-share">
            <Share2 className="h-4 w-4 mr-1.5" /> Share
          </Button>
          <Button className="h-9 bg-slate-900 text-white hover:bg-slate-800" onClick={addToQuote}
            data-testid="collection-add-to-quote">
            <FileText className="h-4 w-4 mr-1.5" /> Add to Quote
          </Button>
          <button onClick={del} className="h-9 w-9 grid place-items-center rounded border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-200"
            data-testid="collection-delete"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      {(col.items || []).length === 0 ? (
        <div className="text-center py-16 border border-dashed rounded-md" data-testid="collection-items-empty">
          <Package className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="text-[14px] text-slate-600">No products in this collection yet.</div>
          <Button onClick={() => setAddOpen(true)} variant="outline" className="mt-4 h-9">
            <Plus className="h-4 w-4 mr-1.5" /> Add products
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md divide-y">
          {(col.items || []).map((it) => (
            <div key={it.product_code} className="p-3 flex items-center gap-3"
              data-testid={`collection-item-${it.product_code}`}>
              <div className="h-11 w-11 rounded bg-slate-100 grid place-items-center overflow-hidden shrink-0">
                {it.image_url ? <img src={it.image_url} alt="" className="h-full w-full object-contain" />
                  : <Package className="h-5 w-5 text-slate-300" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-medium text-slate-900 truncate">
                  {it.name || it.product_code}
                  {it.missing && <span className="ml-2 text-[10px] text-rose-600">(not in catalogue)</span>}
                </div>
                <div className="text-[11px] text-slate-500 font-mono">{it.part_number || it.product_code}</div>
              </div>
              <div className="text-[13px] font-mono text-slate-700 w-28 text-right">{fmtINR(it.list_price_inr)}</div>
              <div className="flex items-center gap-1 border border-slate-200 rounded h-8 px-1">
                <button onClick={() => setQty(it.product_code, it.quantity - 1)}
                  className="h-6 w-6 text-slate-600 hover:text-slate-900"
                  data-testid={`collection-dec-${it.product_code}`}>–</button>
                <span className="w-8 text-center text-[12px]">{it.quantity}</span>
                <button onClick={() => setQty(it.product_code, it.quantity + 1)}
                  className="h-6 w-6 text-slate-600 hover:text-slate-900"
                  data-testid={`collection-inc-${it.product_code}`}>+</button>
              </div>
              <button onClick={() => removeItem(it.product_code)}
                className="h-7 w-7 rounded hover:bg-slate-100 text-slate-400 hover:text-rose-600 grid place-items-center"
                data-testid={`collection-remove-${it.product_code}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {addOpen && (
        <AddProductsPanel onClose={() => setAddOpen(false)} onAdd={addProduct}
          inCollection={new Set((col.items || []).map((i) => i.product_code))} />
      )}
      {editMeta && (
        <EditMetaDialog col={col} onClose={() => setEditMeta(false)}
          onSaved={(data) => { setCol(data); setEditMeta(false); }} cid={cid} />
      )}
      {shareOpen && (
        <ShareDialog cid={cid} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}


function AddProductsPanel({ onClose, onAdd, inCollection }) {
  useEscape(onClose);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/catalogue-v2/products",
          { params: { q: q || undefined, limit: 60 } });
        if (!cancelled) setRows(data.items || []);
      } catch { if (!cancelled) setRows([]); }
      finally { if (!cancelled) setBusy(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}
      data-testid="add-products-panel">
      <div className="w-full max-w-lg bg-white h-full overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-slate-900">Add products</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="relative mt-4">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
            placeholder="Search the catalogue by name, model, part number…" className="h-9 pl-8"
            data-testid="add-products-search" />
        </div>
        <div className="mt-4 space-y-2">
          {busy && rows.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-[13px]">Searching…</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-[13px]">No products match.</div>
          ) : rows.map((p) => {
            const added = inCollection.has(p.code);
            return (
              <div key={p.code} className="border border-slate-200 rounded-md p-3 flex items-center gap-3"
                data-testid={`add-prod-${p.code}`}>
                <div className="h-10 w-10 rounded bg-slate-100 grid place-items-center overflow-hidden shrink-0">
                  {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-contain" />
                    : <Package className="h-4 w-4 text-slate-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-slate-900 truncate">{p.name}</div>
                  <div className="text-[11px] text-slate-500">{p.brand_code} · {fmtINR(p.list_price_inr)}</div>
                </div>
                <Button size="sm" variant={added ? "outline" : "default"}
                  className={`h-8 ${added ? "" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                  onClick={() => onAdd(p, 1)} data-testid={`add-prod-btn-${p.code}`}>
                  {added ? "+1" : <><Plus className="h-3.5 w-3.5 mr-1" />Add</>}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function EditMetaDialog({ col, cid, onClose, onSaved }) {
  useEscape(onClose);
  const [name, setName] = useState(col.name || "");
  const [description, setDescription] = useState(col.description || "");
  const [tags, setTags] = useState((col.tags || []).join(", "));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/catalogue-collections/${cid}`, {
        name: name.trim(), description: description.trim() || "",
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Saved");
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="edit-meta-dialog">
      <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-slate-900">Edit collection</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Name *</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-collection-name" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Description</div>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="edit-collection-desc" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Tags (comma separated)</div>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} data-testid="edit-collection-tags" />
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" className="flex-1 h-10" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 h-10 bg-slate-900 text-white hover:bg-slate-800"
            onClick={save} disabled={busy} data-testid="edit-collection-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function ShareDialog({ cid, onClose }) {
  useEscape(onClose);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPrices, setShowPrices] = useState(false);
  const [expiry, setExpiry] = useState("30");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/catalogue-collections/${cid}/shares`); setLinks(data.items || []); }
    catch { setLinks([]); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/catalogue-collections/${cid}/shares`,
        { show_prices: showPrices, expires_days: Number(expiry) });
      toast.success("Share link created");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  const revoke = async (sid) => {
    try { await api.post(`/catalogue-collections/share/${sid}/revoke`); toast.success("Link turned off"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };
  const copy = async (url) => {
    const full = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    try { await navigator.clipboard.writeText(full); toast.success("Link copied"); }
    catch { toast.error("Copy failed — " + full); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="share-dialog">
      <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Globe className="h-5 w-5 text-slate-700" /> Share catalogue
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-[12px] text-slate-500 mt-1">
          Create a customer-safe public link. Anyone with the link can view the products; internal costs are never shown.
        </p>

        <div className="mt-4 border border-slate-200 rounded-md p-3 space-y-3">
          <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
            <input type="checkbox" checked={showPrices} onChange={(e) => setShowPrices(e.target.checked)}
              data-testid="share-show-prices" />
            Show prices on the public page
          </label>
          <div className="flex items-center gap-2 text-[13px] text-slate-700">
            <span>Link expires in</span>
            <select value={expiry} onChange={(e) => setExpiry(e.target.value)}
              className="h-8 border border-slate-200 rounded px-2 text-[13px] bg-white" data-testid="share-expiry">
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="0">Never (until revoked)</option>
            </select>
          </div>
          <Button className="h-9 bg-slate-900 text-white hover:bg-slate-800" onClick={create}
            disabled={busy} data-testid="share-create">
            <Share2 className="h-4 w-4 mr-1.5" /> {busy ? "Creating…" : "Create link"}
          </Button>
        </div>

        <div className="mt-4 space-y-2 max-h-56 overflow-auto">
          {links.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-[13px]">No links yet.</div>
          ) : links.map((l) => (
            <div key={l.id} className="border border-slate-200 rounded-md p-3" data-testid={`share-link-${l.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[12px] font-mono text-slate-800 truncate">{l.url_path}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {l.status === "ACTIVE" ? "Active" : "Revoked"}
                    {l.show_prices ? " · prices shown" : " · prices hidden"}
                    {l.expires_at ? ` · expires ${String(l.expires_at).slice(0, 10)}` : " · no expiry"}
                    {` · ${l.view_count} views · ${l.enquiry_count} enquiries`}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {l.status === "ACTIVE" && (
                    <>
                      <button onClick={() => copy(l.url)} title="Copy link"
                        className="h-8 w-8 grid place-items-center rounded border border-slate-200 text-slate-500 hover:text-slate-900"
                        data-testid={`share-copy-${l.id}`}><Copy className="h-3.5 w-3.5" /></button>
                      <a href={l.url_path} target="_blank" rel="noreferrer" title="Open"
                        className="h-8 w-8 grid place-items-center rounded border border-slate-200 text-slate-500 hover:text-slate-900"
                        data-testid={`share-open-${l.id}`}><Globe className="h-3.5 w-3.5" /></a>
                      <button onClick={() => revoke(l.id)} title="Turn off"
                        className="h-8 w-8 grid place-items-center rounded border border-slate-200 text-slate-400 hover:text-rose-600"
                        data-testid={`share-revoke-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
